import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useCallback, useEffect, useMemo } from 'react'

import useGlobalSearchContext from 'hooks/GlobalSearch/useGlobalSearchContext'
import { getHotelsPopularDestinations, getIsFetchingFeaturedDestinations } from 'selectors/destinationSelectors'
import GlobalSearchState, { GlobalSearchStateActions, GLOBAL_SEARCH_INITIAL_STATE } from 'contexts/GlobalSearch/GlobalSearchState'
import { fetchFeaturedDestinations } from 'actions/DestinationAlertsActions'
import { SEARCH_VERTICALS } from 'constants/search'
import { encodeSearchParams } from 'lib/search/searchUtils'
import { getCheckInDateFromURLSearchParams, getCheckOutDateFromURLSearchParams, getOccupanciesFromURLSearchParams, getSearchItemFromURLSearchParams } from 'lib/url/searchUrlUtils'
import config from 'constants/config'
import { HOMES_AND_VILLAS_OFFER_TYPES } from 'constants/offerList'

export function useGlobalHomesAndVillasSearch(
  initialState: GlobalSearchState = GLOBAL_SEARCH_INITIAL_STATE,
  syncWithURLSearchParams?: URLSearchParams,
) {
  const queryParams = useMemo(() => {
    if (syncWithURLSearchParams) {
      return {
        checkinDate: getCheckInDateFromURLSearchParams(syncWithURLSearchParams),
        checkoutDate: getCheckOutDateFromURLSearchParams(syncWithURLSearchParams),
        occupancies: getOccupanciesFromURLSearchParams(syncWithURLSearchParams),
        searchItem: getSearchItemFromURLSearchParams(syncWithURLSearchParams),
      }
    }
    return undefined
  }, [syncWithURLSearchParams])

  const { globalSearchState, globalSearchDispatch } = useGlobalSearchContext({
    ...initialState,
    disableMultiRoom: true,
    disableSaleUnit: true,
    disableRecentSearches: true,
    occupancies: initialState.occupancies?.length ? initialState.occupancies : GLOBAL_SEARCH_INITIAL_STATE.occupancies,
    searchVerticals: new Set([SEARCH_VERTICALS.HOMES_AND_VILLAS]),
  })

  // sync check in date
  useEffect(() => {
    if (queryParams) {
      const { checkinDate } = queryParams
      if (checkinDate) {
        globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: queryParams.checkinDate })
      } else {
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams?.checkinDate])

  // sync check out date
  useEffect(() => {
    if (queryParams) {
      const { checkoutDate } = queryParams
      if (checkoutDate) {
        globalSearchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: queryParams.checkoutDate })
      } else {
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams?.checkoutDate])

  // sync occupancies
  useEffect(() => {
    if (queryParams) {
      const { occupancies } = queryParams
      if (occupancies?.length) {
        globalSearchDispatch({ type: GlobalSearchStateActions.SET_OCCUPANCIES, occupancies })
      } else {
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_OCCUPANCIES })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams?.occupancies])

  // sync search item
  useEffect(() => {
    if (queryParams) {
      const { searchItem } = queryParams
      if (searchItem) {
        globalSearchDispatch({ type: GlobalSearchStateActions.SET_SEARCH_ITEM, searchItem })
      } else {
        globalSearchDispatch({ type: GlobalSearchStateActions.UNSET_SEARCH_ITEM })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams?.searchItem])

  const dispatch = useAppDispatch()

  const isFetchingFeaturedDestinations = useAppSelector((state) => getIsFetchingFeaturedDestinations(state))
  const hotelsPopularDestinations = useAppSelector((state) => getHotelsPopularDestinations(state))

  useEffect(() => {
    globalSearchDispatch({ type: GlobalSearchStateActions.UNPOPULATE_POPULAR_DESTINATIONS })
    dispatch(fetchFeaturedDestinations())
  }, [dispatch, globalSearchDispatch])

  useEffect(() => {
    if (!isFetchingFeaturedDestinations) {
      globalSearchDispatch({
        type: GlobalSearchStateActions.POPULATE_POPULAR_DESTINATIONS,
        popularDestinations: hotelsPopularDestinations,
      })
    }
  }, [isFetchingFeaturedDestinations, hotelsPopularDestinations, globalSearchDispatch])

  return {
    globalHomesAndVillasSearchDispatch: globalSearchDispatch,
    globalHomesAndVillasSearchState: globalSearchState,
  }
}

export function useGlobalHomesAndVillasTrendingDestinations(props?: { search?: string, filters?: App.OfferListFilters }) {
  const { filters, search } = props

  const getTrendingDesintationOption = useCallback((placeId: string, label: string) => {
    const searchItem: App.SearchItem = {
      searchType: 'destination',
      value: placeId,
      format: {
        mainText: label,
      },
    }
    const queryParams = encodeSearchParams({
      urlSearch: search,
      searchItem,
      rooms: [config.search.defaultOccupants],
      filters: { ...filters, offerTypes: HOMES_AND_VILLAS_OFFER_TYPES },
      isFlexibleWithDate: true,
    }).toString()

    return {
      value: placeId,
      text: label,
      url: `/search/homes-and-villas?${queryParams}`,
    }
  }, [filters, search])

  return useMemo(() => [
    // getTrendingDesintationOption('le_735b90b4568125ed6c3f678819b6e058', 'Greece'),
    getTrendingDesintationOption('le_8f85517967795eeef66c225f7883bdcb', 'Thailand'),
    getTrendingDesintationOption('le_64930b7151b6bc3e289f37d5da62ac9f', 'Bali'),
    // getTrendingDesintationOption('le_757b505cfd34c64c85ca5b5690ee5293', 'United States'),
    // getTrendingDesintationOption('le_d3d9446802a44259755d38e6d163e820', 'Australia'),
    // getTrendingDesintationOption('le_17e62166fc8586dfa4d1bc0e1742c08b', 'Croatia'),
    // getTrendingDesintationOption('le_cfecdb276f634854f3ef915e2e980c31', 'United Kingdom'),
  ], [getTrendingDesintationOption])
}
